import React, { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Copy, Download, ExternalLink } from 'lucide-react';
import { ErrorState, LoadingState, SeverityBadge, Tab, TabList, TabPanel, Tabs, describeError } from '@/components/primitives';
import WorldMap from '@/components/map/WorldMap';
import useResource from '@/pages/dashboard/useResource';
import { apiGet, copyText, downloadFile } from '@/pages/dashboard/api';
import { ago } from '@/pages/dashboard/format';
import { SourceTag, SourceTags, TypeBadge, defang, plural } from './actorUi';
import '@/styles/actors.css';

/*
 * One threat actor. Overview answers "who, why, against whom"; Techniques and Malware are the
 * ATT&CK picture with procedure text; Activity, Indicators and the "observed" blocks are what
 * IntelliSecure's own reporting shows. Each fact carries its source.
 */

const TABS = ['overview', 'techniques', 'software', 'activity', 'indicators', 'references'];

function Fact({ label, value, source, empty = 'Not known' }) {
  return (
    <div className="ac-fact">
      <dt>{label}</dt>
      <dd>{value || value === 0 ? value : <span className="ac-muted">{empty}</span>}{value ? <SourceTag source={source} /> : null}</dd>
    </div>
  );
}

function Overview({ p, ins, rel }) {
  const [more, setMore] = useState(false);
  const desc = p.description || '';
  const long = desc.length > 900;
  const targetCodes = p.targets.countries.map((c) => c.iso2);
  const observed = ins.countries.map((c) => c.iso2);
  const mapValues = useMemo(() => {
    const v = {};
    targetCodes.forEach((c) => { v[c] = 2; });
    observed.forEach((c) => { v[c] = Math.max(v[c] || 0, 1); });
    return v;
  }, [targetCodes, observed]);
  const pv = p.provenance;

  return (
    <div className="ac-grid">
      <div className="ac-col">
        <section className="ac-card">
          <h2 className="ac-h2">Summary</h2>
          {p.summary && <p className="ac-lead">{p.summary} <SourceTag source={pv.summary} /></p>}
          {desc ? (
            <>
              <div className={`ac-desc${long && !more ? ' is-clamped' : ''}`}>{desc.split(/\n\n+/).map((para, i) => <p key={i}>{para}</p>)}</div>
              {long && <button type="button" className="ac-more" onClick={() => setMore(!more)}>{more ? 'Show less' : 'Read the full description'}</button>}
              <span className="ac-fine">Description: <SourceTag source={pv.description} /></span>
            </>
          ) : !p.summary && <p className="ac-muted">No description yet.</p>}
        </section>

        <section className="ac-card">
          <h2 className="ac-h2">Attribution</h2>
          <dl className="ac-facts">
            <Fact label="Origin" value={p.origin?.name} source={pv.origin_country} empty="Not attributed" />
            <Fact label="Sponsor or affiliation" value={p.sponsor} source={pv.sponsor} />
            <Fact label="Motivation" value={p.motivations.map((m) => m.label).join(', ')} source={pv.motivations} />
            <Fact label="Active since" value={p.first_seen} source={pv.first_seen} />
            <Fact label="Sophistication" value={p.sophistication} source={pv.sophistication} empty="Not assessed" />
            <Fact label="Attribution confidence" value={p.attribution_confidence} source={pv.attribution_confidence} empty="Not assessed" />
          </dl>
        </section>

        {p.campaigns.length > 0 && (
          <section className="ac-card">
            <h2 className="ac-h2">Campaigns <span className="ac-h2-n">{p.campaigns.length}</span></h2>
            <ul className="ac-campaigns">
              {p.campaigns.map((c) => (
                <li key={c.id}>
                  <div className="ac-campaign-h">
                    <a href={c.url} target="_blank" rel="noopener noreferrer">{c.name}</a>
                    <span className="ac-muted">{[c.first_seen, c.last_seen].filter(Boolean).join(' to ')}</span>
                  </div>
                  <p>{(c.description || '').slice(0, 320)}{(c.description || '').length > 320 ? '…' : ''}</p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <div className="ac-col">
        {rel.reasons.length > 0 && (
          <section className="ac-card ac-why">
            <h2 className="ac-h2">Why it matters to you</h2>
            <ul>{rel.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
          </section>
        )}
        <section className="ac-card">
          <h2 className="ac-h2">Targeting</h2>
          {(targetCodes.length > 0 || observed.length > 0) && (
            <WorldMap values={mapValues} fit={[...new Set([...targetCodes, ...observed])]} compact height={200}
              label={`Countries ${p.name} targets${observed.length ? ' and countries seen in our reporting' : ''}`} />
          )}
          <dl className="ac-facts ac-facts--stack">
            <div className="ac-fact"><dt>Sectors <SourceTag source={pv.target_sectors} /></dt>
              <dd>{p.targets.sectors.length ? <span className="ac-chips">{p.targets.sectors.map((s) => <span key={s} className="ac-chip">{s}</span>)}</span> : <span className="ac-muted">Not stated</span>}</dd></div>
            <div className="ac-fact"><dt>Countries <SourceTag source={pv.target_countries} /></dt>
              <dd>{p.targets.countries.length ? <span className="ac-chips">{p.targets.countries.map((c) => <span key={c.iso2} className="ac-chip">{c.name}</span>)}</span> : <span className="ac-muted">Not stated</span>}</dd></div>
            {p.targets.regions.length > 0 && <div className="ac-fact"><dt>Regions <SourceTag source={pv.target_regions} /></dt>
              <dd><span className="ac-chips">{p.targets.regions.map((r) => <span key={r} className="ac-chip">{r}</span>)}</span></dd></div>}
          </dl>
          {(ins.countries.length > 0 || ins.sectors.length > 0) && (
            <div className="ac-observed">
              <h3 className="ac-h3">Seen in our reporting <SourceTag source="insights" /></h3>
              {ins.countries.length > 0 && <p>Countries: {ins.countries.map((c) => `${c.name} (${c.count})`).join(', ')}</p>}
              {ins.sectors.length > 0 && <p>Sectors: {ins.sectors.map((s) => `${s.name} (${s.count})`).join(', ')}</p>}
            </div>
          )}
          {(targetCodes.length > 0 || observed.length > 0) && <p className="ac-fine">Darker on the map: stated targets. Lighter: only seen in our reporting.</p>}
        </section>
      </div>
    </div>
  );
}

function Techniques({ p, ins, tactics }) {
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(() => new Set());
  const seen = useMemo(() => new Map(ins.techniques.map((t) => [t.id, t.count])), [ins.techniques]);
  const list = p.techniques.filter((t) => filter === 'all' || (filter === 'seen' ? seen.has(t.id) : t.sources.includes('analyst') || t.sources.includes('insights')));
  // Each technique sits under its primary tactic; anything the catalog no longer has goes last.
  const known = new Set(tactics.map((x) => x.name));
  const groups = [
    ...tactics.map((tac) => ({ tactic: tac.name, items: list.filter((t) => t.tactics[0] === tac.name) })),
    { tactic: 'Other', items: list.filter((t) => !known.has(t.tactics[0])) },
  ].filter((g) => g.items.length);
  const max = Math.max(1, ...p.tactics.map((t) => t.count));
  const toggle = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const reportedOnly = ins.techniques.filter((t) => !t.in_profile);

  return (
    <div className="ac-stack">
      <section className="ac-card">
        <div className="ac-card-h">
          <h2 className="ac-h2">Coverage by tactic</h2>
          <span className="ac-muted">{plural(p.techniques.length, 'technique')}, ATT&CK Enterprise</span>
        </div>
        <ol className="ac-tactics" aria-label="Techniques per tactic">
          {p.tactics.map((t) => (
            <li key={t.name} className={t.count ? '' : 'is-empty'}>
              <span className="ac-tactic-bar" aria-hidden="true"><i style={{ height: `${(100 * t.count) / max}%` }} /></span>
              <b>{t.count}</b>
              <span className="ac-tactic-name">{t.name}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="ac-card">
        <div className="ac-card-h">
          <div className="is-seg" role="group" aria-label="Which techniques">
            {[['all', `All ${p.techniques.length}`], ['seen', `Seen in our reporting ${p.techniques.filter((t) => seen.has(t.id)).length}`], ['added', 'Added by IntelliSecure']].map(([id, l]) => (
              <button key={id} type="button" aria-pressed={filter === id} onClick={() => setFilter(id)}>{l}</button>
            ))}
          </div>
          <span className="ac-muted">From ATT&CK unless marked. Select one for how this group uses it.</span>
        </div>
        {groups.length === 0 ? <p className="ac-muted">No technique matches this view.</p> : groups.map((g) => (
          <div key={g.tactic} className="ac-tgroup">
            <h3 className="ac-h3">{g.tactic} <span className="ac-h2-n">{g.items.length}</span></h3>
            <ul className="ac-techs">
              {g.items.map((t) => {
                const isOpen = open.has(t.id);
                return (
                  <li key={t.id} className={isOpen ? 'is-open' : undefined}>
                    <button type="button" className="ac-tech" aria-expanded={isOpen} onClick={() => toggle(t.id)} data-testid={`tech-${t.id}`}>
                      {isOpen ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
                      <code className="ac-tid">{t.id}</code>
                      <span className="ac-tname">{t.full_name}</span>
                      {seen.has(t.id) && <span className="ac-seen" title="Seen in IntelliSecure reporting">Seen {seen.get(t.id)}×</span>}
                      <SourceTags sources={t.sources} />
                    </button>
                    {isOpen && (
                      <div className="ac-tech-b">
                        {t.procedure ? <p>{t.procedure}</p> : <p className="ac-muted">ATT&CK lists this technique for the group without a procedure description.</p>}
                        {t.note && <p className="ac-note"><b>Analyst note.</b> {t.note}</p>}
                        {t.tactics.length > 1 && <p className="ac-fine">Also under: {t.tactics.slice(1).join(', ')}</p>}
                        <div className="ac-tech-links">
                          {t.url && <a href={t.url} target="_blank" rel="noopener noreferrer">{t.id} on ATT&CK <ExternalLink aria-hidden="true" /></a>}
                          {t.refs?.slice(0, 3).map((r) => r.url && <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer" title={r.title}>{r.source} <ExternalLink aria-hidden="true" /></a>)}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </section>

      {reportedOnly.length > 0 && (
        <section className="ac-card">
          <h2 className="ac-h2">Seen in our reporting, not yet in the profile <SourceTag source="insights" /></h2>
          <p className="ac-fine">Techniques identified in stories attributed to {p.name}. They join the profile once an analyst confirms them.</p>
          <ul className="ac-kv">{reportedOnly.map((t) => <li key={t.id}><span><code className="ac-tid">{t.id}</code> {t.name}</span><b>{t.count}</b></li>)}</ul>
        </section>
      )}
    </div>
  );
}

function Software({ p, ins }) {
  const [type, setType] = useState('all');
  const list = p.software.filter((s) => type === 'all' || s.type === type);
  const counts = { all: p.software.length, malware: p.software.filter((s) => s.type === 'malware').length, tool: p.software.filter((s) => s.type === 'tool').length };
  const reportedOnly = ins.malware.filter((m) => !m.in_profile);
  return (
    <div className="ac-stack">
      <div className="is-seg" role="group" aria-label="Software type">
        {[['all', 'All'], ['malware', 'Malware'], ['tool', 'Tools']].map(([id, l]) => (
          <button key={id} type="button" aria-pressed={type === id} onClick={() => setType(id)}>{l} <span className="ac-count-n">{counts[id]}</span></button>
        ))}
      </div>
      {list.length === 0 ? <div className="ac-empty"><b>No {type === 'all' ? 'software' : type} recorded</b><span>Nothing in ATT&CK or from our analysts yet.</span></div> : (
        <ul className="ac-sw">
          {list.map((s) => (
            <li key={s.key} className="ac-card" data-testid={`sw-${s.key}`}>
              <div className="ac-sw-h">
                <h3 className="ac-sw-name">{s.name}</h3>
                <span className={`ac-swtype ac-swtype--${s.type}`}>{s.type === 'tool' ? 'Tool' : 'Malware'}</span>
                {s.id && <code className="ac-tid">{s.id}</code>}
              </div>
              {s.aliases.length > 0 && <p className="ac-fine">Also known as {s.aliases.slice(0, 5).join(', ')}</p>}
              {s.procedure && <p className="ac-sw-use"><b>How {p.name} uses it.</b> {s.procedure}</p>}
              {s.description && <p className="ac-sw-desc">{s.description}</p>}
              {s.note && <p className="ac-note"><b>Analyst note.</b> {s.note}</p>}
              <div className="ac-sw-f">
                {s.platforms.length > 0 && <span>{s.platforms.slice(0, 4).join(', ')}</span>}
                {s.technique_count > 0 && <span>{plural(s.technique_count, 'technique')}</span>}
                <SourceTags sources={s.sources} />
                {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer">{s.id} on ATT&CK <ExternalLink aria-hidden="true" /></a>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {reportedOnly.length > 0 && (
        <section className="ac-card">
          <h2 className="ac-h2">Named in our reporting, not yet in the profile <SourceTag source="insights" /></h2>
          <ul className="ac-kv">{reportedOnly.map((m) => <li key={m.name}><span>{m.name}{m.software_id ? <code className="ac-tid"> {m.software_id}</code> : ''}</span><b>{plural(m.count, 'story', 'stories')}</b></li>)}</ul>
        </section>
      )}
    </div>
  );
}

function Activity({ p, ins }) {
  const max = Math.max(1, ...ins.monthly.map((m) => m.attributed + m.mentioned));
  const any = ins.counts.attributed + ins.counts.mentioned > 0;
  return (
    <div className="ac-stack">
      <section className="ac-card">
        <div className="ac-card-h">
          <h2 className="ac-h2">Stories per month, last 12 months</h2>
          <span className="ac-legend"><span><i className="is-attr" />Attributed</span><span><i className="is-ment" />Mentioned</span></span>
        </div>
        {any ? (
          <div className="ac-months" role="img" aria-label={`Stories per month: ${ins.monthly.map((m) => `${m.month} ${m.attributed + m.mentioned}`).join(', ')}`}>
            {ins.monthly.map((m) => (
              <div key={m.month} className="ac-month" title={`${m.month}: ${m.attributed} attributed, ${m.mentioned} mentioned`}>
                <span className="ac-month-bar">
                  <i className="is-ment" style={{ height: `${(100 * m.mentioned) / max}%` }} />
                  <i className="is-attr" style={{ height: `${(100 * m.attributed) / max}%` }} />
                </span>
                <span className="ac-month-l">{m.month.slice(5)}</span>
              </div>
            ))}
          </div>
        ) : <p className="ac-muted">IntelliSecure has not reported on {p.name} yet. The profile above comes from ATT&CK and our analysts.</p>}
      </section>

      {ins.stories.length > 0 && (
        <section className="ac-card">
          <h2 className="ac-h2">Stories <span className="ac-h2-n">{ins.counts.attributed} attributed, {ins.counts.mentioned} mentioned</span></h2>
          <ul className="ac-stories">
            {ins.stories.map((s) => (
              <li key={s.id}>
                <SeverityBadge severity={s.severity} size="sm" />
                <span className="ac-story-main">
                  {s.yours ? <Link to={`/threats?id=${encodeURIComponent(s.id)}`}>{s.name}</Link> : <span>{s.name}</span>}
                  <span className="ac-muted">{s.relation === 'attributed' ? 'Attributed' : 'Mentioned'}{s.yours ? ' · In your threats' : ''} · {ago(s.discovered_at)}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {ins.cves.length > 0 && (
        <section className="ac-card">
          <h2 className="ac-h2">Vulnerabilities exploited <span className="ac-h2-n">from attributed stories</span></h2>
          <ul className="ac-kv">
            {ins.cves.map((c) => (
              <li key={c.id}>
                <span><a href={`https://nvd.nist.gov/vuln/detail/${c.id}`} target="_blank" rel="noopener noreferrer"><code className="ac-tid">{c.id}</code></a>{c.kev && <span className="ac-kev">Known exploited</span>}</span>
                <b>{plural(c.count, 'story', 'stories')}</b>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Indicators({ p, ins }) {
  const [type, setType] = useState('all');
  const types = [...new Set(ins.indicators.map((i) => i.type))];
  const list = ins.indicators.filter((i) => type === 'all' || i.type === type);
  const exportCsv = async () => {
    try {
      await downloadFile(`/actors/${p.slug}/indicators.csv`, `${p.slug}-indicators.csv`);
      toast.success('Indicators exported');
    } catch (err) {
      toast.error("Couldn't export indicators", { description: describeError(err) });
    }
  };
  if (!ins.indicators.length) {
    return <div className="ac-empty"><b>No indicators yet</b><span>Indicators appear here when IntelliSecure attributes a story with indicators to {p.name}. Mentions never contribute indicators.</span></div>;
  }
  return (
    <section className="ac-card">
      <div className="ac-card-h">
        <div className="is-seg" role="group" aria-label="Indicator type">
          {['all', ...types].map((t) => <button key={t} type="button" aria-pressed={type === t} onClick={() => setType(t)}>{t === 'all' ? 'All' : t.toUpperCase()}</button>)}
        </div>
        <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={exportCsv}><Download aria-hidden="true" /> Export CSV</button>
      </div>
      <p className="ac-fine">From stories attributed to {p.name}. Shown defanged; the export and the copy button give the original value.</p>
      <ul className="ac-iocs">
        {list.map((i) => (
          <li key={i.value}>
            <span className="ac-ioc-type">{i.type.toUpperCase()}</span>
            <code className="ac-ioc">{defang(i.value)}</code>
            <button type="button" className="ac-icon-btn" aria-label={`Copy ${defang(i.value)}`} onClick={async () => { if (await copyText(i.value)) toast.success('Copied'); }}><Copy aria-hidden="true" /></button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function References({ p }) {
  if (!p.references.length) return <div className="ac-empty"><b>No references</b><span>Neither ATT&CK nor our analysts have cited sources for this profile yet.</span></div>;
  return (
    <section className="ac-card">
      <h2 className="ac-h2">References <span className="ac-h2-n">{p.references.length}</span></h2>
      <ol className="ac-refs">
        {p.references.map((r) => (
          <li key={r.url || r.title}>
            {r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer">{r.title}</a> : <span>{r.title}</span>}
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function ActorProfile() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'overview';
  const setTab = (t) => setParams((x) => { const n = new URLSearchParams(x); if (t === 'overview') n.delete('tab'); else n.set('tab', t); return n; }, { replace: true });
  const res = useResource(() => apiGet(`/actors/${encodeURIComponent(slug)}`), [slug]);
  const d = res.data;

  if (res.loading && !d) return <div className="pg ac"><LoadingState label="threat actor" rows={8} /></div>;
  if (res.error && !d) {
    const notFound = res.error?.response?.status === 404;
    return (
      <div className="pg ac">
        {notFound ? <div className="ac-empty"><b>No profile at this address</b><span>It may have been renamed or withdrawn.</span><Link to="/actors" className="is-btn is-btn--secondary is-btn--sm">All threat actors</Link></div>
          : <ErrorState title="Couldn't load this threat actor" error={res.error} onRetry={res.reload} />}
      </div>
    );
  }
  const { profile: p, insights: ins, relevance: rel } = d;
  const malware = p.software.filter((s) => s.type === 'malware').length;
  const navigator = async () => {
    try {
      await downloadFile(`/actors/${p.slug}/navigator`, `${p.slug}-attack-layer.json`);
      toast.success('ATT&CK Navigator layer downloaded', { description: 'Open it at mitre-attack.github.io/attack-navigator.' });
    } catch (err) {
      toast.error("Couldn't download the layer", { description: describeError(err) });
    }
  };

  return (
    <div className="pg ac" data-testid="actor-profile">
      <header className="ac-head">
        <div className="ac-head-main">
          <div className="ac-head-title">
            <h1 className="t-title">{p.name}</h1>
            <TypeBadge type={p.type} label={p.type_label} />
            {p.mitre?.id && <a className="ac-mitre" href={p.mitre.url} target="_blank" rel="noopener noreferrer" title="Open on MITRE ATT&CK">{p.mitre.id} <ExternalLink aria-hidden="true" /></a>}
          </div>
          {p.aliases.length > 0 && <p className="ac-head-aliases"><span className="ac-muted">Also known as</span> {p.aliases.join(', ')}</p>}
          {p.mitre?.withdrawn && <p className="ac-warn">ATT&CK no longer lists this group. The profile is kept for its history.</p>}
        </div>
        <div className="ac-head-actions">
          <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={navigator} disabled={!p.techniques.length} data-testid="navigator"><Download aria-hidden="true" /> ATT&CK Navigator layer</button>
        </div>
      </header>

      <section className="is-stats" style={{ '--is-stats-n': 5 }} aria-label="Key facts">
        <div className="is-stats-item"><span className="is-stats-l">Origin</span><b className="is-stats-v ac-v-text">{p.origin?.name || 'Not attributed'}</b><span className="is-stats-s">{p.sponsor || (p.motivations.map((m) => m.label).join(', ') || 'Motivation not stated')}</span></div>
        <div className="is-stats-item"><span className="is-stats-l">Active since</span><b className="is-stats-v">{p.first_seen || '-'}</b><span className="is-stats-s">{ins.last_reported ? `Last in our reporting ${ago(ins.last_reported)}` : 'Not in our reporting yet'}</span></div>
        <div className="is-stats-item"><span className="is-stats-l">Techniques</span><b className="is-stats-v">{p.techniques.length}</b><span className="is-stats-s">Across {p.tactics.filter((t) => t.count).length} tactics</span></div>
        <div className="is-stats-item"><span className="is-stats-l">Malware and tools</span><b className="is-stats-v">{p.software.length}</b><span className="is-stats-s">{malware} malware, {p.software.length - malware} tools</span></div>
        <div className="is-stats-item"><span className="is-stats-l">Our reporting</span><b className="is-stats-v">{ins.counts.attributed + ins.counts.mentioned}</b><span className="is-stats-s">{ins.counts.attributed} attributed, {ins.counts.yours} in your threats</span></div>
      </section>

      <Tabs value={tab} onValueChange={setTab}>
        <TabList label="Profile sections">
          <Tab value="overview">Overview</Tab>
          <Tab value="techniques" count={p.techniques.length}>Techniques</Tab>
          <Tab value="software" count={p.software.length}>Malware and tools</Tab>
          <Tab value="activity" count={ins.counts.attributed + ins.counts.mentioned}>Activity</Tab>
          <Tab value="indicators" count={ins.indicators.length}>Indicators</Tab>
          <Tab value="references" count={p.references.length}>References</Tab>
        </TabList>
        <TabPanel value="overview"><Overview p={p} ins={ins} rel={rel} /></TabPanel>
        <TabPanel value="techniques"><Techniques p={p} ins={ins} tactics={d.tactics} /></TabPanel>
        <TabPanel value="software"><Software p={p} ins={ins} /></TabPanel>
        <TabPanel value="activity"><Activity p={p} ins={ins} /></TabPanel>
        <TabPanel value="indicators"><Indicators p={p} ins={ins} /></TabPanel>
        <TabPanel value="references"><References p={p} /></TabPanel>
      </Tabs>

      <p className="ac-attrib">
        {p.mitre ? <>ATT&CK content from <a href={d.attribution.url} target="_blank" rel="noopener noreferrer">{d.attribution.text}</a> v{p.mitre.version}. {d.attribution.notice} </> : null}
        {p.updated_at && `Profile updated ${ago(p.updated_at)}.`}
      </p>
    </div>
  );
}
